import { motion } from "framer-motion";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import Story from "@/components/home/Story";
import Method from "@/components/home/Method";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";

const crumbs = [{ name: "Home", to: "/" }, { name: "About", to: "/about" }];

const PEOPLE_SCHEMA = [
  { "@context": "https://schema.org", "@type": "Person", name: "Angela", jobTitle: "Co-founder", worksFor: { "@type": "Organization", name: "Viral Vision Media" }, description: "Grew up working full time in her family's food manufacturing business from age 16, with first-hand experience of production, wholesale and retail." },
  { "@context": "https://schema.org", "@type": "Person", name: "Baxter", jobTitle: "Co-founder", worksFor: { "@type": "Organization", name: "Viral Vision Media" }, description: "Focuses on growth: strategy, paid advertising, websites and the systems that turn marketing into customers." },
];

const BELIEFS = [
  ["Understand the business first", "We don't start with a content calendar. We start by walking the floor, meeting the team and working out how you actually make money."],
  ["What's normal to you is interesting to them", "The production run, the early start, the founder's knowledge. The everyday parts of a business are usually the most watchable."],
  ["Marketing that fits the operation", "Shoot days that don't stop production. Systems that reduce admin rather than add it. Plans that flex when the big order lands."],
  ["No fake numbers", "We won't show you made up results and we won't invent testimonials. When we have a story worth telling, it's real."],
];

const AboutPage = () => {
  const { items } = useMedia("about", 1);
  return (
    <div data-testid="about-page">
      <Seo title="About Angela & Baxter" description="Viral Vision Media was built by Angela and Baxter. Angela grew up working in her family's food manufacturing business; together they build marketing around how real businesses run. Based in Sydney, serving Australia." schema={[breadcrumbSchema(crumbs), ...PEOPLE_SCHEMA]} />
      <header className="pt-32 md:pt-44 pb-12 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <Breadcrumbs items={crumbs} />
          <p className="mt-10 label-eyebrow text-[#38BDF8]">About</p>
          <h1 className="mt-6 font-display uppercase text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.9] text-[#2E1B16] max-w-5xl">
            Two people who'd rather be <span className="font-accent font-normal normal-case text-[#38BDF8]">on site.</span>
          </h1>
          <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-[#2E1B16]/55" data-testid="about-meta">
            <span>Based in Sydney</span><span aria-hidden="true">·</span>
            <span>Serving businesses Australia-wide</span><span aria-hidden="true">·</span>
            <a href="mailto:info@viralvisionmediaau.com" className="text-[#38BDF8] hover:underline">info@viralvisionmediaau.com</a>
          </p>
        </motion.div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <MediaFrame item={items[0]} ratio="aspect-[16/10]" autoplay hoverPlay={false} className="rounded-sm" sizes="(min-width:1024px) 60vw, 100vw" />
        </Reveal>
        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.1}>
          <p className="font-accent text-2xl md:text-3xl text-[#2E1B16] leading-snug">Angela understands how businesses run. Baxter understands how they grow. VVM is what happens when you put the two in the same room.</p>
        </Reveal>
      </section>

      <Story />

      <section className="bg-[#F2EFE9] py-24 md:py-32" aria-labelledby="beliefs-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>What we believe</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 id="beliefs-heading" className="mt-6 font-display uppercase text-3xl md:text-5xl tracking-tight text-[#2E1B16] max-w-3xl">Four things we won't compromise on.</h2></Reveal>
          <ol className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-12" data-testid="about-beliefs">
            {BELIEFS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <li className="border-t border-[#2E1B16]/12 pt-6">
                  <span className="font-display text-5xl text-[#2E1B16]/10">0{i + 1}</span>
                  <h3 className="mt-2 font-display text-2xl tracking-tight text-[#2E1B16]">{t}</h3>
                  <p className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed">{d}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <Method />
      <PersonalCTA />
      <Contact pageLabel="About page" />
    </div>
  );
};

export default AboutPage;
